<template>
  <DSFieldset
    v-if="props.isGroup"
    :class="DS_FORM_FIELD_CLASSES.Root"
    data-cy="ds-form-field__group"
    :name="props.id"
    :label="displayLabel"
    :required="props.required"
    :disabled="props.disabled"
    :error-messages="props.errorMessages"
    :tooltip="props.tooltip"
    :with-divider="false"
    :hidden-label="props.hiddenLabel"
  >
    <slot :described-by="describedBy" />

    <p
      v-if="props.helperText"
      :id="helperTextId"
      :class="DS_FORM_FIELD_CLASSES.HelperText"
      data-cy="ds-form-field__helper-text"
    >
      {{ props.helperText }}
    </p>

    <template v-if="hasErrors">
      <DSFormFieldError
        v-for="(error, index) in props.errorMessages"
        :id="errorMessageIds[index]"
        :key="`${props.name}-error-${index}`"
        :error-message="error"
      />
    </template>
  </DSFieldset>

  <DSLabel
    v-else
    :class="DS_FORM_FIELD_CLASSES.Root"
    :name="props.id"
    :label="displayLabel"
    :required="props.required"
    :has-error="hasErrors"
    :inline-edit="props.inlineEdit"
    :disabled="props.disabled"
    :tooltip="props.tooltip"
    :hidden-label="props.hiddenLabel"
  >
    <slot :described-by="describedBy" />

    <p
      v-if="props.helperText"
      :id="helperTextId"
      :class="DS_FORM_FIELD_CLASSES.HelperText"
      data-cy="ds-form-field__helper-text"
    >
      {{ props.helperText }}
    </p>

    <template v-if="hasErrors">
      <DSFormFieldError
        v-for="(error, index) in props.errorMessages"
        :id="errorMessageIds[index]"
        :key="`${props.name}-error-${index}`"
        :error-message="error"
      />
    </template>
  </DSLabel>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { DS_FORM_FIELD_CLASSES } from "@/src/design_system/form_elements/form_field/constants/classes";
import DSFieldset from "@/src/design_system/form_elements/fieldset/components/DSFieldset.vue";
import DSLabel from "@/src/design_system/form_elements/label/components/DSLabel.vue";
import DSFormFieldError from "@/src/design_system/form_elements/form_field_error/components/DSFormFieldError.vue";
import type { DSFormFieldProps } from "../types/dsFormField";

const props = withDefaults(defineProps<DSFormFieldProps>(), {
  inlineEdit: false,
  required: false,
  disabled: false,
  errorMessages: undefined,
  helperText: undefined,
  id: undefined,
  label: undefined,
  isGroup: false,
  hiddenLabel: false,
});

const displayLabel = computed(() => props.label ?? "");
const fieldId = computed(() => props.id || props.name);
const hasErrors = computed(() => !!props.errorMessages?.length);
const helperTextId = computed(() => (props.helperText && fieldId.value ? `${fieldId.value}-helper` : undefined));
const errorMessageIds = computed(() => {
  return (props.errorMessages ?? []).map((_, index) => (fieldId.value ? `${fieldId.value}-error-${index}` : undefined));
});
const describedBy = computed(() => {
  const ids = [helperTextId.value, ...errorMessageIds.value].filter(Boolean);

  return ids.length ? ids.join(" ") : undefined;
});
</script>
